import { defineStore } from "pinia";
import { reactive, ref } from "vue";

import { useNewTopic, useTopicReply } from "@/composition/api/messenger.ts";
import { useMessengerAuthor } from "@/composition/messenger/useMessengerAuthor.ts";
import type {
  DpaNewsMessageReply,
  DpaNewsTopicItem,
} from "@/types/messenger/messages";

export const useTopicStore = defineStore("topics", () => {
  const { messengerAuthor } = useMessengerAuthor();
  const topicMessages = ref<DpaNewsTopicItem[]>([]);
  const currentTopic = ref<DpaNewsTopicItem | null>(null);
  const newTopic = reactive({
    messageBody: "",
    messageAuthor: messengerAuthor.value,
  });
  const newTopicReply = reactive({
    replyBody: "",
    topicMessageId: "",
    messageAuthor: messengerAuthor.value,
  });

  function updateTopics(updatedTopic: DpaNewsTopicItem) {
    const store = useTopicStore();
    store.$patch((state) => {
      const index = state.topicMessages.findIndex(
        (topic) => topic.id === updatedTopic.id,
      );

      if (index !== -1) {
        const existingTopic = state.topicMessages[index];
        existingTopic.lastThreadUpdate = updatedTopic.lastThreadUpdate;
        existingTopic.numReplies = updatedTopic.numReplies;
        existingTopic.isNew = true;

        state.topicMessages.splice(index, 1);
        state.topicMessages.unshift(existingTopic);
      } else {
        state.topicMessages.unshift({
          ...updatedTopic,
          isNew: true,
        });
      }
    });
  }

  function updateCurrentTopic(reply: DpaNewsMessageReply) {
    const store = useTopicStore();
    store.$patch((state) => {
      if (state.currentTopic) {
        state.currentTopic.numReplies += 1;
        state.currentTopic?.replies?.push({
          ...reply,
        });
      }
    });
  }

  const { execute: postTopicReply, error: replyTopicError } =
    useTopicReply(newTopicReply);

  async function handlePostReply(replyBody: string, topicId: string) {
    newTopicReply.replyBody = replyBody;
    newTopicReply.topicMessageId = topicId;
    newTopicReply.messageAuthor = messengerAuthor.value;
    await postTopicReply();
  }

  const {
    execute: postTopic,
    data: postedTopic,
    error: postTopicError,
  } = useNewTopic(newTopic);

  async function handlePostTopic(messageBody: string): Promise<any> {
    newTopic.messageBody = messageBody;
    newTopic.messageAuthor = messengerAuthor.value;
    await postTopic();
  }

  return {
    topicMessages,
    currentTopic,
    updateTopics,
    updateCurrentTopic,
    handlePostTopic,
    postTopicError,
    postedTopic,
    handlePostReply,
    replyTopicError,
  };
});
